import { ref } from 'vue';
import { useArloService } from '@/services/arloapi.js';
import { useErrorHandler } from '@/composables/useErrorHandler.js';
import { projetosEndpoint } from '@/api/endpoints/projetos';
import { dashboardEndpoint } from '@/api/endpoints/dashboards';
import { basicosEndpoint } from '@/api/endpoints/basicos';
import { tiposSolicitacoesProjetoEndpoint } from '@/api/endpoints/tipos-solicitacoes-projeto';
import { municipiosEndpoint } from '@/api/endpoints/municipios';
import { gruposEndpoint } from '@/api/endpoints/grupos';
import { pessoasEndpoint } from '@/api/endpoints/pessoas';
import { useCacheApi, PREFIXO_CACHE_DASHBOARD } from '@/composables/cache/useCacheApi';
import { useRecursos } from '@/composables/useRecursos';

export const status_solicitacao_pendente = import.meta.env.VITE_STATUS_SOLICITACAO_PENDENTE;
export const status_solicitacao_aprovada = import.meta.env.VITE_STATUS_SOLICITACAO_APROVADA;
export const status_solicitacao_ajuste = import.meta.env.VITE_STATUS_SOLICITACAO_AJUSTE;
export const status_solicitacao_contratacao = import.meta.env.VITE_STATUS_SOLICITACAO_CONTRATACAO;
export const status_solicitacao_reprovada = import.meta.env.VITE_STATUS_SOLICITACAO_REPROVADA;
export const status_solicitacao_cancelada = import.meta.env.VITE_STATUS_SOLICITACAO_CANCELADA;
export const status_solicitacao_rascunho = import.meta.env.VITE_STATUS_SOLICITACAO_RASCUNHO;
export const status_solicitacao_pagamento = import.meta.env.VITE_STATUS_SOLICITACAO_PAGAMENTO;
export const status_solicitacao_autorizacao = import.meta.env.VITE_STATUS_SOLICITACAO_AUTORIZACAO;
export const status_solicitacao_concluida = import.meta.env.VITE_STATUS_SOLICITACAO_CONCLUIDA;
export const status_solicitacao_fornecimento = import.meta.env.VITE_STATUS_SOLICITACAO_FORNECIMENTO;
export const status_solicitacao_naoautorizada = import.meta.env.VITE_STATUS_SOLICITACAO_NAO_AUTORIZADA;

export const tipo_solicitacao_material_consumo = 'Solicitação de ' + import.meta.env.VITE_TIPO_SOLICITACAO_MATERIAL_CONSUMO;
export const tipo_solicitacao_material_permanente = 'Solicitação de ' + import.meta.env.VITE_TIPO_SOLICITACAO_MATERIAL;
export const tipo_solicitacao_servico = 'Solicitação de ' + import.meta.env.VITE_TIPO_SOLICITACAO_SERVICO;
export const tipo_solicitacao_servico_pessoa_juridica = 'Solicitação de ' + import.meta.env.VITE_TIPO_SOLICITACAO_SERVICO_PESSOA_JURIDICA;
export const tipo_solicitacao_servico_pessoa_fisica = 'Solicitação de ' + import.meta.env.VITE_TIPO_SOLICITACAO_SERVICO_PESSOA_FISICA;
export const tipo_solicitacao_pagamento_servico_pessoa_fisica = 'Solicitação de Pagamento de ' + import.meta.env.VITE_TIPO_SOLICITACAO_SERVICO_PESSOA_FISICA;
export const tipo_solicitacao_pagamento_servico_pessoa_juridica = 'Solicitação de Pagamento de ' + import.meta.env.VITE_TIPO_SOLICITACAO_SERVICO_PESSOA_JURIDICA;

export function useProjeto() {
    const { getDados } = useArloService();
    const { handleApiError } = useErrorHandler();
    const { criarCarregador } = useRecursos();

    const projeto = ref({});
    const dados = ref({});
    const membros = ref([]);
    const metas = ref([]);
    // Solicitações vinculadas a uma meta: envelope paginado `{ items, total }`.
    const metasRelacionadas = ref([]);
    const aditivos = ref([]);
    const desembolsos = ref([]);
    const contasBancarias = ref([]);
    const todasContasBancarias = ref([]);
    const tiposSolicitacoesDisponiveis = ref([]);

    const solicitacoes = ref([]);
    const solicitacoesDoa = ref([]);
    const saldoDoa = ref({});

    const rubricas = ref([]);
    const rubricasRetiradaRendimentos = ref([]);
    const rubricasDetalhes = ref({});
    const rubricaDetalhamento = ref({});
    const subrubricas = ref([]);

    const rubricasOld = ref([]); // Mantido para compatibilidade
    const listaAnalistasProjeto = ref([]);

    const statusHistorico = ref([]);
    const historicoAlteracoes = ref([]);
    const paginacaoHistoricoAlteracoes = ref({ page: 1, pages: 1, size: 0, total: 0 });
    const historicoAlteracoesAditivo = ref([]);
    const paginacaoHistoricoAlteracoesAditivo = ref({ page: 1, pages: 1, size: 0, total: 0 });
    const listaApoiadas = ref([]);
    const comprovantesRelatorio = ref([]);

    const relatorio = ref(null);
    const valorDevolvidoRelatorio = ref(null);
    const loadingEntidadesRelacionadas = ref(false);
    const loadingMetasRelacionadas = ref(false);

    const carregarRelatorioSolicitacao = criarCarregador(relatorio, (solicitacaoProjetoId) => projetosEndpoint.relatoriosSolicitacao.obterRelatorioSolicitacao(solicitacaoProjetoId));

    const carregarComprovantesRelatorio = async (relatorioId) => {
        try {
            const response = await getDados(projetosEndpoint.relatoriosSolicitacao.listarComprovantes(relatorioId));
            comprovantesRelatorio.value = response.value?.comprovantes ?? [];
            valorDevolvidoRelatorio.value = response.value?.valor_devolvido ?? null;
        } catch (err) {
            handleApiError(err);
        }
    }

    const carregarAnalistasProjeto = criarCarregador(listaAnalistasProjeto, gruposEndpoint.listarPessoasFisicas('analistasprojeto'));

    const carregarProjeto = criarCarregador(projeto, (projetoId) => projetosEndpoint.projeto.obterCabecalho(projetoId));

    const carregarDados = criarCarregador(dados, (projetoId) => projetosEndpoint.projeto.obterDados(projetoId));

    const carregarMembros = criarCarregador(membros, (projetoId, status) => projetosEndpoint.membros.listar(projetoId) + (status ? `?status=${encodeURIComponent(status)}` : ''));

    const carregarMembrosBolsasValidas = criarCarregador(membros, (projetoId) => projetosEndpoint.membros.listarComBolsasValidas(projetoId));

    const carregarMembrosPagamento = criarCarregador(membros, (projetoId) => projetosEndpoint.membros.listarComParcelasAPagar(projetoId));

    const carregarDesembolsos = criarCarregador(desembolsos, (projetoId) => projetosEndpoint.desembolsos.listar(projetoId));

    const carregarRubricasOld = criarCarregador(rubricasOld, (projetoId) => projetosEndpoint.rubricas.listar(projetoId));

    const carregarRubricas = criarCarregador(rubricas, (projetoId) => projetosEndpoint.rubricas.listarCabecalho(projetoId));

    const carregarRubricasRetiradaRendimentos = criarCarregador(rubricasRetiradaRendimentos, (projetoId) => projetosEndpoint.rubricas.listarRetiradaRendimentos(projetoId));

    const carregarRubricasDisponiveis = criarCarregador(rubricas, (projetoId, tipo_solicitacao_id) => projetosEndpoint.rubricasDetalhamento.listarPorTipoSolicitacao(projetoId, tipo_solicitacao_id));

    const carregarRubricasPorTipoSolicitacao = criarCarregador(rubricas, (tipo_solicitacao_id) => tiposSolicitacoesProjetoEndpoint.tiposSolicitacoesProjeto.listarRubricasPorTipo(tipo_solicitacao_id));

    const carregarRubricasDetalhes = criarCarregador(rubricasDetalhes, (projetoId, rubricaId) => projetosEndpoint.rubricasDetalhamento.listar(projetoId, rubricaId));

    const carregarRubricasDetalhesPorCodigo = criarCarregador(rubricasDetalhes, (projetoId, codigoRubrica) => projetosEndpoint.rubricasDetalhamento.listarPorCodigo(projetoId, codigoRubrica));

    const carregarRubricaDetalhamento = criarCarregador(rubricaDetalhamento, (rubricaId) => projetosEndpoint.rubricasDetalhamento.obter(rubricaId));

    const carregarSubrubricas = criarCarregador(subrubricas, (rubricaDetalheId) => basicosEndpoint.rubricasFinanceiras.listarSubrubricasPorDetalheRubricaId(rubricaDetalheId));

    const carregarEntidadeRelacionadas = criarCarregador(solicitacoes, (detalhesId, page = 1, size = 50) => projetosEndpoint.rubricasDetalhamento.listarEntidadesRelacionadas(detalhesId, page, size), { carregando: loadingEntidadesRelacionadas });

    const carregarMetasRelacionadas = criarCarregador(metasRelacionadas, (metaId, page = 1, size = 50) => projetosEndpoint.metas.listarMetasRelacionadas(metaId, page, size), { carregando: loadingMetasRelacionadas });

    const carregarSolicitacoes = async (projetoId, tipo = null, options = {}) => {
        try {
            const { page = null, size = null, search = null, tipo_solicitacao_status_id = null, membro_id = null, validas = false, filtros = null, tiposolicitacaopagamentoclt_id = null, tipo_solicitacao_id = null } = options;

            if (tipo === 'doa') {
                const response = await getDados(projetosEndpoint.solicitacoesDoa.listar(projetoId));
                solicitacoesDoa.value = response.value
            } else if (tipo === 'rascunhos') {
                const response = await getDados(projetosEndpoint.solicitacoes.listarRascunhos(projetoId));
                solicitacoes.value = response.value
            } else if (tipo === 'emprestimo') {
                const params = new URLSearchParams();
                if (options.a_pagar) {
                    params.append('a_pagar', options.a_pagar);
                }
                const query = params.toString() ? `?${params.toString()}` : '';
                const response = await getDados(`${projetosEndpoint.solicitacoesEmprestimo.listar(projetoId)}${query}`);
                solicitacoes.value = response.value
            } else if (tipo === 'pagamentos_emprestimos') {
                const response = await getDados(projetosEndpoint.solicitacoesPagamentoEmprestimo.listarPorSolicitacaoEmprestimo(projetoId));
                solicitacoes.value = response.value
            } else if (tipo === 'transferencias_bancarias') {
                const response = await getDados(projetosEndpoint.solicitacoesTransferenciaBancariaEntreContas.listarPorTransferenciaBancariaEntreContas(projetoId));
                solicitacoes.value = response.value
            } else if (tipo === 'receitas_encerramento') {
                const response = await getDados(projetosEndpoint.solicitacoesReceitaPorEncerramento.listarPorReceitaPorEncerramento(projetoId));
                solicitacoes.value = response.value
            } else if (tipo === 'clt') {
                const params = new URLSearchParams();
                if (tiposolicitacaopagamentoclt_id) {
                    params.append(
                        'tiposolicitacaopagamentoclt_id',
                        tiposolicitacaopagamentoclt_id
                    );
                }
                const query = params.toString()
                    ? `?${params.toString()}`
                    : '';
                const response = await getDados(
                    projetosEndpoint.solicitacoesClt.listar(projetoId, query)
                );
                solicitacoes.value = response.value;
            } else {
                // Construir query params
                const params = new URLSearchParams();

                if (page) params.append('page', page);
                if (size) params.append('size', size);
                if (search) params.append('search', search);
                if (tipo_solicitacao_id) params.append('tipo_solicitacao_id', tipo_solicitacao_id);
                if (tipo_solicitacao_status_id) params.append('tipo_solicitacao_status_id', tipo_solicitacao_status_id);
                if (membro_id) params.append('membro_id', membro_id);
                if (validas) params.append('validas', validas);
                if (filtros) params.append(filtros);
                const queryString = params.toString();
                const endpoint = `${projetosEndpoint.solicitacoes.listar(projetoId)}${queryString ? '?' + queryString : ''}`;

                const response = await getDados(endpoint);
                solicitacoes.value = response.value;
            }
        } catch (err) {
            handleApiError(err);
        }
    }

    const carregarSolicitacoesNaoPaginada = async (projetoId, options = {}) => {
        try {
            //TODO: Ajustar endpoint para não paginado no backend
            const response = await getDados(`${projetosEndpoint.solicitacoes.listar(projetoId)}${options ? '?' + new URLSearchParams(options).toString() : ''}`);
            solicitacoes.value = response.value.items;
        } catch (err) {
            handleApiError(err);
        }
    }

    const carregarAditivos = criarCarregador(aditivos, (projetoId) => projetosEndpoint.aditivos.listar(projetoId));

    const carregarMetas = criarCarregador(metas, (projetoId) => projetosEndpoint.metas.listar(projetoId));

    const carregarContasBancarias = criarCarregador(contasBancarias, (projetoId) => projetosEndpoint.contasBancarias.listar(projetoId));

    const carregarTodasContasBancarias = criarCarregador(todasContasBancarias, (projetoId) => `${projetosEndpoint.contasBancarias.listarTodas}?projeto_id=${projetoId}`);

    const carregarSaldoDoa = criarCarregador(saldoDoa, (projetoId) => projetosEndpoint.solicitacoesDoa.saldoDoa(projetoId));

    const carregarStatusHistorico = criarCarregador(statusHistorico, (projetoId) => projetosEndpoint.statusHistorico.listar(projetoId));

    const carregarHistoricoAlteracoes = criarCarregador(historicoAlteracoes, (projetoId, page = 1, size = 50) => projetosEndpoint.historicoAlteracoes.listar(projetoId, page, size), {
        extrair: 'items',
        paginacao: paginacaoHistoricoAlteracoes,
    });

    const carregarHistoricoAlteracoesAditivo = criarCarregador(historicoAlteracoesAditivo, (aditivoId, page = 1, size = 10) => projetosEndpoint.aditivos.listarHistoricoAlteracoes(aditivoId, page, size), {
        extrair: 'items',
        paginacao: paginacaoHistoricoAlteracoesAditivo,
    });

    const carregarTiposSolicitacoesDisponiveis = criarCarregador(tiposSolicitacoesDisponiveis, (projetoId) => projetosEndpoint.solicitacoes.obterTiposSolicitacaoDisponiveis(projetoId));

    const carregarInstituicoesApoiadas = criarCarregador(listaApoiadas, projetosEndpoint.instituicaoApoiada.listar);

    return {
        projeto,
        dados,
        membros,
        metas,
        aditivos,
        solicitacoes,
        solicitacoesDoa,
        rubricas,
        rubricasRetiradaRendimentos,
        rubricasOld,
        desembolsos,
        rubricasDetalhes,
        rubricaDetalhamento,
        subrubricas,
        contasBancarias,
        todasContasBancarias,
        saldoDoa,
        listaAnalistasProjeto,
        statusHistorico,
        historicoAlteracoes,
        paginacaoHistoricoAlteracoes,
        historicoAlteracoesAditivo,
        paginacaoHistoricoAlteracoesAditivo,
        tiposSolicitacoesDisponiveis,
        relatorio,
        comprovantesRelatorio,
        valorDevolvidoRelatorio,
        loadingEntidadesRelacionadas,
        loadingMetasRelacionadas,
        metasRelacionadas,
        listaApoiadas,

        carregarProjeto,
        carregarComprovantesRelatorio,
        carregarRelatorioSolicitacao,
        carregarTiposSolicitacoesDisponiveis,
        carregarAnalistasProjeto,
        carregarMembros,
        carregarRubricas,
        carregarRubricasRetiradaRendimentos,
        carregarRubricasOld,
        carregarRubricasDetalhes,
        carregarRubricaDetalhamento,
        carregarRubricasPorTipoSolicitacao,
        carregarSolicitacoes,
        carregarDados,
        carregarMetas,
        carregarAditivos,
        carregarDesembolsos,
        carregarContasBancarias,
        carregarTodasContasBancarias,
        carregarSaldoDoa,
        carregarSubrubricas,
        carregarRubricasDetalhesPorCodigo,
        carregarRubricasDisponiveis,
        carregarEntidadeRelacionadas,
        carregarMetasRelacionadas,
        carregarMembrosPagamento,
        carregarSolicitacoesNaoPaginada,
        carregarMembrosBolsasValidas,
        carregarStatusHistorico,
        carregarHistoricoAlteracoes,
        carregarHistoricoAlteracoesAditivo,
        carregarInstituicoesApoiadas,
    }
}

export function useBasicosProjetos() {
    const { getDados } = useArloService();
    const { handleApiError } = useErrorHandler();
    const { criarCarregador } = useRecursos();
    const { buscarComCache } = useCacheApi(PREFIXO_CACHE_DASHBOARD);

    const listaTiposStatus = ref([]);
    const listaTiposSolicitacoes = ref([]);
    const listaTiposSolicitacoesProjetos = ref([]);
    const listaModalidadeBolsa = ref([]);
    const listaTipoCargos = ref([]);
    const listaTipoMembro = ref([]);
    const listaTiposStatusSolicitacoesClt = ref([]);
    const listaMunicipios = ref([]);
    const listaMunicipiosDescricao = ref([]);
    const listaTiposAditivos = ref([]);

    const carregarMunicipios = criarCarregador(listaMunicipios, (filtros = null) => `${municipiosEndpoint.listarMunicipios}${filtros ? `?${filtros}` : ''}`);

    const carregarMunicipiosDescricao = criarCarregador(listaMunicipiosDescricao, (descricao) => municipiosEndpoint.obterPorDescricao(descricao));

    const carregarTipoMembro = criarCarregador(listaTipoMembro, basicosEndpoint.tiposMembros.listar);

    const carregarTiposCargos = criarCarregador(listaTipoCargos, basicosEndpoint.tiposCargos.listar);

    const carregarModalidadeBolsa = criarCarregador(listaModalidadeBolsa, basicosEndpoint.modalidadesBolsas.listar);

    const carregarTiposAditivos = criarCarregador(listaTiposAditivos, basicosEndpoint.tiposAditivos.listar);

    const carregarTiposStatus = criarCarregador(listaTiposStatus, basicosEndpoint.statusProjetos.listar);

    /**
     * @param {object} [opcoes]
     * @param {boolean} [opcoes.cache] - Cacheia por 10 min. Ligado só no painel
     *   inicial; as telas de cadastro que usam esta lista continuam buscando do
     *   servidor a cada abertura.
     */
    const carregarTiposSolicitacoes = async ({ cache = false, ...opcoes } = {}) => {
        try {
            const endpoint = basicosEndpoint.statusSolicitacoes.listar;
            listaTiposSolicitacoes.value = cache
                ? await buscarComCache(endpoint, opcoes)
                : (await getDados(endpoint)).value;
        } catch (err) {
            handleApiError(err);
        }
    }

    const carregarTiposSolicitacoesProjetos = criarCarregador(listaTiposSolicitacoesProjetos, basicosEndpoint.tipoSolicitacoesProjetos.listar);

    const carregarTiposStatusSolicitacoesClt = criarCarregador(listaTiposStatusSolicitacoesClt, basicosEndpoint.statusSolicitacoesClt.listar);

    const verificarCoordenacao = async (projetoId, usuarioLogadoId) => {

        if (!projetoId || !usuarioLogadoId) return false;

        const usuarioId = Number(usuarioLogadoId);

        try {
            const response = await getDados(projetosEndpoint.projeto.obterCabecalho(projetoId));
            const coordenadorId = Number(response.value?.coordenador_id);
            const ehSubcoordenador = (response.value?.subcoordenadores ?? [])
                .some((sub) => Number(sub.pessoa_fisica_id) === usuarioId);

            return coordenadorId === usuarioId || ehSubcoordenador;
        } catch (err) {
            handleApiError(err);
            return false;
        }
    }

    return {
        listaTiposStatus,
        listaTiposSolicitacoes,
        listaTiposSolicitacoesProjetos,
        listaTipoCargos,
        listaModalidadeBolsa,
        listaTipoMembro,
        listaTiposStatusSolicitacoesClt,
        listaMunicipios,
        listaMunicipiosDescricao,
        listaTiposAditivos,

        carregarTiposStatus,
        carregarTiposSolicitacoes,
        carregarTiposSolicitacoesProjetos,
        carregarTiposCargos,
        carregarModalidadeBolsa,
        carregarTipoMembro,
        carregarTiposStatusSolicitacoesClt,
        carregarMunicipios,
        carregarMunicipiosDescricao,
        carregarTiposAditivos,
        verificarCoordenacao,
    }
}

export function useProjetos() {
    const { getDados } = useArloService();
    const { handleApiError } = useErrorHandler();
    const { criarCarregador } = useRecursos();

    const listaProjetos = ref([]);
    const listaProjetosVencer = ref([]);
    const totalProjetos = ref(0);
    const listaSolicitacoes = ref({});
    const listaDashboard = ref({});
    const listaDesembolsos = ref({});
    const listaTodasContasBancarias = ref([]);
    const listaDesembolsosVencer = ref([]);
    const listaRelatoriosViagemPendentes = ref([]);
    const listaMembrosPendentes = ref([]);
    const listaAnalistasProjeto = ref([]);

    const carregarProjetosVencer = async (analista_responsavel_id = null, diasParaVencer = 30) => {
        try {
            const hoje = new Date();
            const dataVencer = new Date(hoje.getTime() + diasParaVencer * 24 * 60 * 60 * 1000);
            const dia = String(dataVencer.getDate()).padStart(2, '0');
            const mes = String(dataVencer.getMonth() + 1).padStart(2, '0');
            const ano = dataVencer.getFullYear();
            const dataFormatada = `${ano}-${mes}-${dia}`;

            let endpoint = `${projetosEndpoint.projeto.listar}?a_vencer=${dataFormatada}`;
            if (analista_responsavel_id) {
                endpoint += `&analistaresponsavel_id=${analista_responsavel_id}`;
            }
            const response = await getDados(endpoint);
            listaProjetosVencer.value = response.value?.items ?? response.value ?? [];
        } catch (err) {
            handleApiError(err);
        }
    }

    const carregarAnalistasProjeto = criarCarregador(listaAnalistasProjeto, gruposEndpoint.listarPessoasFisicas('analistasprojeto'));

    const carregarProjetos = async ({ filtros = null, page = 1, size = 50, analistaresponsavel_id = null, tem_coordenacoes = null, status = null, search = null } = {}) => {
        try {
            const params = new URLSearchParams();

            params.append('page', page);
            params.append('size', size);
            if (analistaresponsavel_id) params.append('analistaresponsavel_id', analistaresponsavel_id);
            if (tem_coordenacoes) params.append('tem_coordenacoes', tem_coordenacoes);
            if (status) {
                const statusValue = Array.isArray(status) ? status.join(', ') : status;
                params.append('status', statusValue);
            }
            if (search) {
                params.append('search', search);
            }
            if (filtros) {
                const filtrosParams = new URLSearchParams(filtros);
                filtrosParams.forEach((value, key) => {
                    params.append(key, value);
                });
            }

            const queryString = params.toString();
            const endpoint = `${projetosEndpoint.projeto.listar}${queryString ? '?' + queryString : ''}`;

            const response = await getDados(endpoint);
            listaProjetos.value = response.value.items ?? response.value;
            totalProjetos.value = response.value.total ?? 0;
        } catch (err) {
            handleApiError(err);
        }
    }

    const carregarProjetosResumo = criarCarregador(listaProjetos, (filtros = null, page = null, size = null) => {
        const params = new URLSearchParams();

        if (page) params.append('page', page);
        if (size) params.append('size', size);
        if (filtros) {
            // Se filtros for uma string de query params, adiciona cada parâmetro
            const filtrosParams = new URLSearchParams(filtros);
            filtrosParams.forEach((value, key) => {
                params.append(key, value);
            });
        }

        const queryString = params.toString();
        return `${projetosEndpoint.projeto.listarResumidos}${queryString ? '?' + queryString : ''}`;
    });

    const carregarTodosProjetos = async () => {
        try {
            const response = await getDados(projetosEndpoint.projeto.listar);
            listaProjetos.value = response.value?.items ?? response.value ?? [];
        } catch (err) {
            handleApiError(err);
        }
    }

    const carregarDesembolsos = criarCarregador(listaDesembolsos, (filtros = null) => `${projetosEndpoint.desembolsos.listarTodos}${filtros ? `?${filtros}` : ''}`);

    const carregarDesembolsosVencer = async (analista_responsavel_id = null, diasParaVencer = 30) => {
        try {
            const hoje = new Date();
            const dataVencer = new Date(hoje.getTime() + diasParaVencer * 24 * 60 * 60 * 1000);
            const dia = String(dataVencer.getDate()).padStart(2, '0');
            const mes = String(dataVencer.getMonth() + 1).padStart(2, '0');
            const ano = dataVencer.getFullYear();
            const dataFormatada = `${ano}-${mes}-${dia}`;

            let endpoint = `${projetosEndpoint.desembolsos.listarTodos}?a_vencer=${dataFormatada}`;
            if (analista_responsavel_id) {
                endpoint += `&analista_responsavel_id=${analista_responsavel_id}`;
            }
            const response = await getDados(endpoint);
            listaDesembolsosVencer.value = (response.value ?? []).filter(item => !item.quitado);
        } catch (err) {
            handleApiError(err);
        }
    }

    const carregarRelatoriosViagemPendentes = criarCarregador(listaRelatoriosViagemPendentes, (analista_responsavel_id = null) => `${projetosEndpoint.relatoriosSolicitacao.listarTodos}${analista_responsavel_id ? `?analista_responsavel_id=${analista_responsavel_id}` : ''}`);

    const carregarMembrosPendentes = criarCarregador(listaMembrosPendentes, (analista_responsavel_id = null) => `${projetosEndpoint.membros.listarPendentes}${analista_responsavel_id ? `?analista_responsavel_id=${analista_responsavel_id}` : ''}`);

    const carregarSolicitacoes = criarCarregador(listaSolicitacoes, (page = 1, size = 10, tipo_solicitacao_status_id = null, analista_responsavel_id = null, search = null) =>
        `${projetosEndpoint.solicitacoes.listarTodas}?page=${page}&size=${size}`
        + (tipo_solicitacao_status_id ? `&tipo_solicitacao_status_id=${tipo_solicitacao_status_id}` : '')
        + (analista_responsavel_id ? `&analista_responsavel_id=${analista_responsavel_id}` : '')
        + (search ? `&search=${encodeURIComponent(search)}` : ''));

    const carregarSolicitacoesCompras = criarCarregador(listaSolicitacoes, ({
        page = 1,
        size = 10,
        tipo_solicitacao_status_id = null,
        tipo_solicitacao_id = null,
        projeto_id = null,
        analista_responsavel_id = null,
        search = null,
    } = {}) => {
        const params = new URLSearchParams({ exige_contratacao: 'true', page, size });
        if (tipo_solicitacao_status_id) params.append('tipo_solicitacao_status_id', tipo_solicitacao_status_id);
        if (tipo_solicitacao_id) params.append('tipo_solicitacao_id', tipo_solicitacao_id);
        if (projeto_id) params.append('projeto_id', projeto_id);
        if (analista_responsavel_id) params.append('analista_responsavel_id', analista_responsavel_id);
        if (search) params.append('search', search);

        return `${projetosEndpoint.solicitacoes.listarTodas}?${params.toString()}`;
    });

    const carregarSolicitacoesDoa = criarCarregador(listaSolicitacoes, (projeto_id) => projetosEndpoint.solicitacoesDoa.listar(projeto_id));

    const carregarAnalistaDashboard = criarCarregador(listaDashboard, dashboardEndpoint.analistaProjetos);

    const carregarSolicitacoesAjuste = criarCarregador(listaSolicitacoes, projetosEndpoint.solicitacoes.listarAjuste);

    const carregarContasBancariasGlobal = criarCarregador(listaTodasContasBancarias, projetosEndpoint.contasBancarias.listarTodas);

    return {
        listaProjetos,
        listaProjetosVencer,
        totalProjetos,
        listaDashboard,
        listaSolicitacoes,
        listaDesembolsos,
        listaDesembolsosVencer,
        listaRelatoriosViagemPendentes,
        listaMembrosPendentes,
        listaAnalistasProjeto,
        listaTodasContasBancarias,

        carregarProjetos,
        carregarProjetosResumo,
        carregarTodosProjetos,
        carregarProjetosVencer,
        carregarSolicitacoes,
        carregarSolicitacoesCompras,
        carregarAnalistaDashboard,
        carregarDesembolsos,
        carregarDesembolsosVencer,
        carregarRelatoriosViagemPendentes,
        carregarMembrosPendentes,
        carregarSolicitacoesDoa,
        carregarSolicitacoesAjuste,
        carregarAnalistasProjeto,
        carregarContasBancariasGlobal,
    }
}

export function useSolicitacao() {
    const { criarCarregador } = useRecursos();

    const solicitacao = ref({});
    const pagamentos = ref({});

    const carregarSolicitacao = criarCarregador(solicitacao, (solicitacaoId) => projetosEndpoint.solicitacoes.obter(solicitacaoId), { quando: (solicitacaoId) => Boolean(solicitacaoId) });
    
    const carregarPagamentoSolicitacao = criarCarregador(pagamentos, (solicitacaoId) => projetosEndpoint.solicitacoes.listarPagamentos(solicitacaoId));

    return {
        solicitacao,
        pagamentos,
        carregarSolicitacao,
        carregarPagamentoSolicitacao,
    }
}

export function usePagamento() {
    const { getDados } = useArloService();
    const { handleApiError } = useErrorHandler();

    const detalhesRubricaValor = ref({});

    const carregarDetalhesRubrica = async (id) => {
        try {
            const response = await getDados(projetosEndpoint.rubricasDetalhamento.obter(id));
            detalhesRubricaValor.value = response.value;
        } catch (err) {
            handleApiError(err);
            throw err;
        }
    }

    return {
        detalhesRubricaValor,
        carregarDetalhesRubrica,
    }
}

export function useMembro() {
    const { getDados } = useArloService();
    const { handleApiError } = useErrorHandler();
    const { criarCarregador } = useRecursos();
    const { buscarComCache } = useCacheApi(PREFIXO_CACHE_DASHBOARD);

    const membro = ref({});
    const anexos = ref([]);
    const bolsas = ref([]);
    const contratos = ref([]);

    const carregarMembro = criarCarregador(membro, (membroId) => projetosEndpoint.membros.obterMembro(membroId));

    const carregarAnexosMembro = criarCarregador(anexos, (membroId) => projetosEndpoint.membros.listarAnexos(membroId));

    const carregarBolsasMembro = criarCarregador(bolsas, (membroId) => projetosEndpoint.membros.listarBolsasRecebidas(membroId));

    /**
     * @param {number} pessoaFisicaId
     * @param {object} [opcoes]
     * @param {boolean} [opcoes.cache] - Cacheia por 10 min. Ligado só no painel
     *   inicial: a listagem de contratos em Pessoas precisa do dado do momento.
     */
    const carregarContratos = async (pessoaFisicaId, { cache = false, ...opcoes } = {}) => {
        try {
            const endpoint = pessoasEndpoint.contratos.listar(pessoaFisicaId);
            contratos.value = cache
                ? await buscarComCache(endpoint, opcoes)
                : (await getDados(endpoint)).value;
        } catch (err) {
            handleApiError(err);
        }
    }

    return {
        membro,
        anexos,
        bolsas,
        contratos,

        carregarMembro,
        carregarAnexosMembro,
        carregarBolsasMembro,
        carregarContratos,
    }
}

export function useInstrumentoJuridico() {
    const { getDados } = useArloService();
    const { handleApiError } = useErrorHandler();
    const { criarCarregador } = useRecursos();
    const { buscarComCache } = useCacheApi(PREFIXO_CACHE_DASHBOARD);

    const instrumentoJuridico = ref({});
    const listaInstrumentosJuridicos = ref([]);
    const anexos = ref([]);
    const tiposInstrumentosJuridicos = ref([]);

    /**
     * @param {object} [opcoes]
     * @param {boolean} [opcoes.cache] - Cacheia por 10 min. Ligado só quando a
     *   lista aparece dentro do painel inicial; na rota própria, que cadastra e
     *   exclui itens, a listagem precisa refletir a ação recém-feita.
     */
    const carregarInstrumentosJuridicos = async (page = 1, size = 10, search = null, possuiProjeto = null, { cache = false, ...opcoes } = {}) => {
        try {
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('size', size);
            if (search) params.append('search', search);
            if (possuiProjeto !== null) params.append('possui_projeto', possuiProjeto);

            const queryString = params.toString();
            const endpoint = `${projetosEndpoint.instrumentoJuridico.listar}?${queryString}`;

            listaInstrumentosJuridicos.value = cache
                ? await buscarComCache(endpoint, opcoes)
                : (await getDados(endpoint)).value;
        } catch (err) {
            handleApiError(err);
        }
    }

    const carregarInstrumentoJuridico = criarCarregador(instrumentoJuridico, (instrumentoJuridicoId) => projetosEndpoint.instrumentoJuridico.obter(instrumentoJuridicoId));

    const carregarAnexosInstrumentoJuridico = criarCarregador(anexos, (instrumentoJuridicoId) => projetosEndpoint.instrumentoJuridico.listarAnexos(instrumentoJuridicoId));

    const carregarTiposInstrumentosJuridicos = criarCarregador(tiposInstrumentosJuridicos, basicosEndpoint.tiposInstrumentosJuridicos.listar);

    return {
        instrumentoJuridico,
        listaInstrumentosJuridicos,
        anexos,
        tiposInstrumentosJuridicos,
        carregarInstrumentosJuridicos,
        carregarInstrumentoJuridico,
        carregarAnexosInstrumentoJuridico,
        carregarTiposInstrumentosJuridicos,
    }
}